import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  InputNumber,
  Message,
  Modal,
  Panel,
  SelectPicker,
  Stack,
  Table,
  toaster,
} from 'rsuite';

import { useDeleteUserRate, useProjects, useUpsertUserRate, useUser, useUserRates } from '../hooks/useQueries';

const { Column, HeaderCell, Cell } = Table;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const currencyInputFormatter = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `$${n.toFixed(2)}`;
};

const currencyInputParser = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  return String(value).replace(/[^0-9.-]/g, '');
};

const CurrentBadge = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1px 8px',
      borderRadius: 999,
      border: '1px solid #b7eb8f',
      background: '#f6ffed',
      color: '#389e0d',
      fontSize: 11,
      fontWeight: 800,
      lineHeight: '16px',
      whiteSpace: 'nowrap',
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
    }}
  >
    Current
  </span>
);

export default function UserDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const userId = Number(params?.userId);

  useEffect(() => {
    if (location?.hash !== '#rates') return;
    // Let the page render before attempting to scroll.
    window.setTimeout(() => {
      const el = document.getElementById('rates');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [location?.hash]);

  const { data: user, isLoading: userLoading, error: userError } = useUser(Number.isInteger(userId) ? userId : null);
  const { data: rates = [], isLoading: ratesLoading, error: ratesError } = useUserRates(
    Number.isInteger(userId) ? userId : null
  );

  const { data: projects = [], isLoading: projectsLoading, error: projectsError } = useProjects();

  const upsertRate = useUpsertUserRate({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Rate saved.
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    },
  });

  const [suppressDeleteRateToast, setSuppressDeleteRateToast] = useState(false);
  const deleteRate = useDeleteUserRate({
    onSuccess: async () => {
      if (suppressDeleteRateToast) return;
      toaster.push(
        <Message showIcon type="success" closable>
          Rate removed.
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    },
  });

  const [addRateOpen, setAddRateOpen] = useState(false);
  const [newProjectId, setNewProjectId] = useState(null);
  const [newRate, setNewRate] = useState(0);
  const [newEffectiveDate, setNewEffectiveDate] = useState(() => new Date());

  const [ratesProjectFilterId, setRatesProjectFilterId] = useState(null);
  const [ratesOnlyCurrent, setRatesOnlyCurrent] = useState(false);

  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingEffectiveDateOriginal, setEditingEffectiveDateOriginal] = useState(null);
  const [editingEffectiveDateDraft, setEditingEffectiveDateDraft] = useState(null);
  const [editingRate, setEditingRate] = useState(0);
  const [editingField, setEditingField] = useState(null); // 'rate' | 'effectiveDate'

  const normalizeIso10 = (raw) => {
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      const yyyy = raw.getFullYear();
      const mm = String(raw.getMonth() + 1).padStart(2, '0');
      const dd = String(raw.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    const s = String(raw ?? '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };

  const iso10ToDate = (iso10) => {
    const s = normalizeIso10(iso10);
    if (!s) return null;
    const yyyy = Number(s.slice(0, 4));
    const mm = Number(s.slice(5, 7));
    const dd = Number(s.slice(8, 10));
    if (!Number.isInteger(yyyy) || !Number.isInteger(mm) || !Number.isInteger(dd)) return null;
    return new Date(yyyy, mm - 1, dd);
  };

  const projectOptions = useMemo(
    () => (projects || []).map((p) => ({ label: p.name, value: Number(p.id) })),
    [projects]
  );

  const currentEffectiveDateByProjectId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(rates) ? rates : [];
    for (const r of rows) {
      const pid = Number(r?.projectId);
      const eff = normalizeIso10(r?.effectiveDate);
      if (!Number.isInteger(pid) || !eff) continue;
      const prev = map.get(pid);
      if (!prev || String(eff).localeCompare(String(prev)) > 0) {
        map.set(pid, eff);
      }
    }
    return map;
  }, [rates]);

  const visibleRates = useMemo(() => {
    const rows = Array.isArray(rates) ? rates : [];
    const pid = ratesProjectFilterId;
    const base = Number.isInteger(pid) ? rows.filter((r) => Number(r?.projectId) === pid) : rows;

    if (!ratesOnlyCurrent) return base;

    return base.filter((r) => {
      const projId = Number(r?.projectId);
      const eff = normalizeIso10(r?.effectiveDate);
      if (!Number.isInteger(projId) || !eff) return false;
      const currentEff = currentEffectiveDateByProjectId.get(projId);
      return Boolean(currentEff) && eff === currentEff;
    });
  }, [rates, ratesProjectFilterId, ratesOnlyCurrent, currentEffectiveDateByProjectId]);

  const openAddRate = () => {
    const preferredProjectId = Number.isInteger(ratesProjectFilterId) ? ratesProjectFilterId : null;
    setNewProjectId(preferredProjectId);
    setNewRate(0);
    setNewEffectiveDate(new Date());
    setAddRateOpen(true);
  };

  const submitAddRate = async () => {
    if (!Number.isInteger(userId)) return;
    if (!Number.isInteger(newProjectId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a project.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const normalized = Number(newRate);
    const rate = Number.isFinite(normalized) ? normalized : 0;

    const effectiveDate = normalizeIso10(newEffectiveDate);
    if (!effectiveDate) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please choose an effective date.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await upsertRate.mutateAsync({ userId, projectId: newProjectId, rate, effectiveDate });
    setAddRateOpen(false);
  };

  const beginEditRate = (row) => {
    const pid = Number(row?.projectId);
    const effectiveDate = normalizeIso10(row?.effectiveDate);
    if (!Number.isInteger(pid)) return;
    if (!effectiveDate) return;
    setEditingProjectId(pid);
    setEditingEffectiveDateOriginal(effectiveDate);
    setEditingEffectiveDateDraft(iso10ToDate(effectiveDate));
    setEditingRate(Number(row?.rate ?? 0));
    setEditingField('rate');
  };

  const beginEditEffectiveDate = (row) => {
    const pid = Number(row?.projectId);
    const effectiveDate = normalizeIso10(row?.effectiveDate);
    if (!Number.isInteger(pid)) return;
    if (!effectiveDate) return;
    setEditingProjectId(pid);
    setEditingEffectiveDateOriginal(effectiveDate);
    setEditingEffectiveDateDraft(iso10ToDate(effectiveDate));
    setEditingRate(Number(row?.rate ?? 0));
    setEditingField('effectiveDate');
  };

  const cancelEditRate = () => {
    setEditingProjectId(null);
    setEditingEffectiveDateOriginal(null);
    setEditingEffectiveDateDraft(null);
    setEditingRate(0);
    setEditingField(null);
  };

  const commitEditRate = async () => {
    if (!Number.isInteger(userId)) {
      cancelEditRate();
      return;
    }

    const projectId = Number(editingProjectId);
    if (!Number.isInteger(projectId)) {
      cancelEditRate();
      return;
    }

    const effectiveDate = normalizeIso10(editingEffectiveDateOriginal);
    if (!effectiveDate) {
      cancelEditRate();
      return;
    }

    const normalized = Number(editingRate);
    const rate = Number.isFinite(normalized) ? normalized : 0;
    await upsertRate.mutateAsync({ userId, projectId, rate, effectiveDate });
    cancelEditRate();
  };

  const commitEditEffectiveDate = async (overrideDate) => {
    if (!Number.isInteger(userId)) {
      cancelEditRate();
      return;
    }

    const projectId = Number(editingProjectId);
    if (!Number.isInteger(projectId)) {
      cancelEditRate();
      return;
    }

    const oldEffectiveDate = normalizeIso10(editingEffectiveDateOriginal);
    if (!oldEffectiveDate) {
      cancelEditRate();
      return;
    }

    const newEffectiveDate = normalizeIso10(overrideDate ?? editingEffectiveDateDraft);
    if (!newEffectiveDate) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please choose an effective date.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (newEffectiveDate === oldEffectiveDate) {
      cancelEditRate();
      return;
    }

    const normalized = Number(editingRate);
    const rate = Number.isFinite(normalized) ? normalized : 0;

    // Persist by creating/updating the new date row, then removing the old date row.
    await upsertRate.mutateAsync({ userId, projectId, rate, effectiveDate: newEffectiveDate });
    try {
      setSuppressDeleteRateToast(true);
      await deleteRate.mutateAsync({ userId, projectId, effectiveDate: oldEffectiveDate });
    } finally {
      setSuppressDeleteRateToast(false);
    }
    cancelEditRate();
  };

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: '0 auto' }}>
      <Panel
        bordered
        header={
          <Stack justifyContent="space-between" alignItems="center" wrap>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#666' }}>User</div>
              <div style={{ fontSize: 18, fontWeight: 700, lineHeight: '22px' }}>
                {user?.name ?? (userLoading ? 'Loading…' : 'User')}
              </div>
            </div>
            <Stack spacing={10} wrap>
              <Button appearance="subtle" onClick={() => navigate(-1)}>
                Back
              </Button>
              <Button appearance="ghost" onClick={openAddRate} disabled={!Number.isInteger(userId)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600 }}>Add Rate</span>
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </span>
              </Button>
            </Stack>
          </Stack>
        }
      >
        {userError ? <Message type="error" showIcon>Failed to load user.</Message> : null}

        <Divider />

        {ratesError ? <Message type="error" showIcon>Failed to load rates.</Message> : null}
        {projectsError ? <Message type="error" showIcon>Failed to load projects.</Message> : null}

        <Panel
          bordered
          id="rates"
          header={
            <Stack justifyContent="space-between" alignItems="center" wrap>
              <span>Rates (per project)</span>
              <Stack direction="row" spacing={10} alignItems="center" wrap>
                <div style={{ fontSize: 12, color: '#666' }}>Project:</div>
                <SelectPicker
                  searchable
                  cleanable
                  placeholder={projectsLoading ? 'Loading…' : 'All projects'}
                  style={{ minWidth: 280 }}
                  data={projectOptions}
                  value={ratesProjectFilterId}
                  onChange={(v) => {
                    if (v === null || v === undefined || v === '') {
                      setRatesProjectFilterId(null);
                      return;
                    }
                    const n = Number(v);
                    setRatesProjectFilterId(Number.isInteger(n) ? n : null);
                  }}
                  disabled={projectsLoading || projectOptions.length === 0}
                />

                <Checkbox checked={ratesOnlyCurrent} onChange={(_value, checked) => setRatesOnlyCurrent(checked)}>
                  Only show current
                </Checkbox>
              </Stack>
            </Stack>
          }
        >
          <Table
            data={visibleRates}
            autoHeight
            bordered
            cellBordered
            loading={ratesLoading}
            rowKey={(r) => `${r.userId}-${r.projectId}-${String(r.effectiveDate || '')}`}
            rowHeight={48}
            headerHeight={38}
          >
            <Column flexGrow={1} minWidth={280}>
              <HeaderCell>Project</HeaderCell>
              <Cell dataKey="projectName" />
            </Column>

            <Column width={160} align="center">
              <HeaderCell>Effective</HeaderCell>
              <Cell>
                {(rowData) => {
                  const pid = Number(rowData?.projectId);
                  const effectiveDate = normalizeIso10(rowData?.effectiveDate);
                  const isEditing =
                    editingField === 'effectiveDate' &&
                    Number.isInteger(pid) &&
                    pid === Number(editingProjectId) &&
                    effectiveDate &&
                    effectiveDate === normalizeIso10(editingEffectiveDateOriginal);

                  if (isEditing) {
                    return (
                      <div
                        style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void commitEditEffectiveDate();
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEditRate();
                          }
                        }}
                      >
                        <DatePicker
                          oneTap
                          format="yyyy-MM-dd"
                          style={{ width: '100%' }}
                          value={editingEffectiveDateDraft}
                          onChange={(v) => {
                            setEditingEffectiveDateDraft(v);
                            // Auto-save immediately when the user selects a date.
                            queueMicrotask(() => {
                              void commitEditEffectiveDate(v);
                            });
                          }}
                          cleanable={false}
                          size="sm"
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditEffectiveDate(rowData);
                      }}
                      title="Click to edit"
                    >
                      {rowData?.effectiveDate || '—'}
                    </div>
                  );
                }}
              </Cell>
            </Column>

            <Column width={200} align="right">
              <HeaderCell>Rate</HeaderCell>
              <Cell>
                {(rowData) => {
                  const pid = Number(rowData?.projectId);
                  const effectiveDate = normalizeIso10(rowData?.effectiveDate);
                  const currentEffective = Number.isInteger(pid) ? currentEffectiveDateByProjectId.get(pid) : null;
                  const isCurrent = Boolean(effectiveDate && currentEffective && effectiveDate === currentEffective);
                  const isEditing =
                    editingField === 'rate' &&
                    Number.isInteger(pid) &&
                    pid === Number(editingProjectId) &&
                    effectiveDate &&
                    effectiveDate === normalizeIso10(editingEffectiveDateOriginal);

                  if (isEditing) {
                    return (
                      <div onClick={(e) => e.stopPropagation()}>
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          step={1}
                          precision={2}
                          value={editingRate}
                          onChange={setEditingRate}
                          formatter={currencyInputFormatter}
                          parser={currencyInputParser}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitEditRate();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditRate();
                            }
                          }}
                          onBlur={() => {
                            void commitEditRate();
                          }}
                          size="sm"
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 10,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginEditRate(rowData);
                      }}
                      title="Click to edit"
                    >
                      {isCurrent ? <CurrentBadge /> : null}
                      {currencyFormatter.format(Number(rowData?.rate ?? 0))}
                    </div>
                  );
                }}
              </Cell>
            </Column>

            <Column width={90} align="center" fixed="right">
              <HeaderCell />
              <Cell>
                {(rowData) => (
                  Number(rowData?.projectId) === Number(editingProjectId) &&
                  normalizeIso10(rowData?.effectiveDate) === normalizeIso10(editingEffectiveDateOriginal) ? (
                    <Stack direction="row" spacing={6} alignItems="center" justifyContent="center">
                      <Button
                        size="xs"
                        appearance="subtle"
                        loading={upsertRate.isPending}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try {
                            if (editingField === 'effectiveDate') await commitEditEffectiveDate();
                            else await commitEditRate();
                          } catch {
                            toaster.push(
                              <Message type="error" showIcon>
                                Failed to save.
                              </Message>,
                              { placement: 'topEnd', duration: 3500 }
                            );
                          }
                        }}
                        title="Save"
                      >
                        <i className="bi bi-check2" aria-hidden="true" />
                      </Button>
                      <Button
                        size="xs"
                        appearance="subtle"
                        disabled={upsertRate.isPending}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          cancelEditRate();
                        }}
                        title="Cancel"
                      >
                        <i className="bi bi-x" aria-hidden="true" />
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      size="xs"
                      appearance="subtle"
                      loading={deleteRate.isPending}
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          await deleteRate.mutateAsync({
                            userId,
                            projectId: Number(rowData?.projectId),
                            effectiveDate: normalizeIso10(rowData?.effectiveDate),
                          });
                        } catch {
                          toaster.push(
                            <Message type="error" showIcon>
                              Failed to remove rate.
                            </Message>,
                            { placement: 'topEnd', duration: 3500 }
                          );
                        }
                      }}
                      title="Delete"
                    >
                      <i className="bi bi-trash" aria-hidden="true" />
                    </Button>
                  )
                )}
              </Cell>
            </Column>
          </Table>

          {!ratesLoading && Array.isArray(rates) && rates.length === 0 ? (
            <div style={{ marginTop: 10, color: '#666', fontSize: 12 }}>No rates yet.</div>
          ) : null}
        </Panel>
      </Panel>

      <Modal open={addRateOpen} onClose={() => setAddRateOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Add Rate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Project</div>
              <SelectPicker
                cleanable={false}
                searchable
                placeholder={projectsLoading ? 'Loading projects…' : 'Select a project'}
                style={{ width: '100%' }}
                data={projectOptions}
                value={Number.isInteger(newProjectId) ? newProjectId : null}
                onChange={(v) => setNewProjectId(Number.isInteger(v) ? v : null)}
                disabled={projectsLoading || projectOptions.length === 0}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Effective date</div>
              <DatePicker
                oneTap
                style={{ width: '100%' }}
                value={newEffectiveDate}
                onChange={setNewEffectiveDate}
                format="MM/dd/yyyy"
                placeholder="Select effective date"
                cleanable={false}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Rate</div>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1}
                precision={2}
                value={newRate}
                onChange={setNewRate}
                formatter={currencyInputFormatter}
                parser={currencyInputParser}
                onFocus={(e) => {
                  queueMicrotask(() => e?.target?.select?.());
                }}
                onClick={(e) => {
                  queueMicrotask(() => e?.target?.select?.());
                }}
                disabled={!Number.isInteger(newProjectId)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={submitAddRate} loading={upsertRate.isPending}>
            Save
          </Button>
          <Button appearance="subtle" onClick={() => setAddRateOpen(false)} disabled={upsertRate.isPending}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
